import { useChat } from "@ai-sdk/react";
import { useAuth } from "@clerk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  Loader2,
  Lock,
  Plus,
  RotateCcw,
  Square,
  Trash2,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  toast,
} from "@ndd/ui";
import * as Sentry from "@sentry/react";
import type { Components } from "react-markdown";
import { cn } from "@/lib/utils";
import { fetchWithAuthRetry } from "@/lib/api";
import { MarkdownRenderer } from "@/components/markdown-renderer";
import { formatTimestamp, formatLocalDate } from "../../../../shared/local-date";
import { deriveThreadTitle } from "../../../../shared/thread-title";
import { AnalysisPill } from "./analysis-pill";
import { TranscriptForm } from "./transcript-form";
import { LargePasteBanner, LARGE_PASTE_THRESHOLD } from "./large-paste-banner";
import { SourceFlyover } from "./source-flyover";
import { RecordSourceFlyover } from "./record-source-flyover";
import { ReadsBlock, readRowLabel, type ReadRowSource } from "./read-row";
import { SourceLedgerLine } from "./source-ledger-line";
import { StarterPromptGroups } from "./starter-prompt-groups";
import {
  renderCiteTokens,
  parseCiteHref,
  stripCiteTokens,
} from "../../../../shared/citations";
import {
  rdFirstName,
  sourceStarterPrompts,
  type StarterPromptGroup,
} from "../../../../shared/starter-prompts";
import type { SessionRowData } from "./session-row";
import type { AnalysisStatus } from "../../../../shared/analysis-types";
import type {
  WorkspaceThreadMessage,
  WorkspaceThreadResponse,
} from "../../../../shared/api-types";
import type { ReadSourceRef } from "../../../../shared/chat-sources";

/** The thread's ad-hoc/playbook_run kind (shared api-types union). */
type ChatKind = WorkspaceThreadResponse["kind"];
/** One persisted chat-message row from the thread endpoint. */
type ChatMessageRow = WorkspaceThreadMessage;

type ChatMessageMetadata = { createdAt?: string };
type ChatUIMessage = UIMessage<ChatMessageMetadata>;

const CHAT_TURN_TIMEOUT_MS = 180_000;
const MAX_THREAD_TITLE_LENGTH = 120;

interface SessionPointerData {
  sessionId: string;
  sessionDate: string | null;
  clientName: string | null;
  summary: string | null;
  status: AnalysisStatus;
  errorMessage: string | null;
}

function getTextContent(message: ChatUIMessage): string {
  return message.parts
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
}

function dbMessageId(id: string) {
  return `db-${id}`;
}

function dbToUIMessages(rows: ChatMessageRow[]): ChatUIMessage[] {
  return rows.map((row) => ({
    id: dbMessageId(row.id),
    role: row.role,
    metadata: { createdAt: row.createdAt },
    // Pointer/read rows render as pills/read logs keyed by message id, so
    // their text must stay empty or it would double-render.
    parts: [{ type: "text" as const, text: rowHasSource(row) ? "" : row.content }],
  }));
}

function rowHasSource(row: ChatMessageRow): boolean {
  return Boolean(
    row.sessionId || row.formSnapshotId || row.observationNoteId ||
      row.meetingNoteId || row.sourceDeletedType,
  );
}

function sourceFromRow(row: ChatMessageRow): ReadRowSource | null {
  if (row.sourceDeletedType && row.sourceDeletedId) {
    return { sourceId: row.sourceDeletedId, sourceType: row.sourceDeletedType, deleted: true, date: null };
  }
  if (row.sessionId) {
    return { sourceId: row.sessionId, sourceType: "session", date: row.sessionDate, clientName: row.clientName };
  }
  if (row.formSnapshotId) {
    return { sourceId: row.formSnapshotId, sourceType: "form", date: row.formPeriodStartDate, formType: row.formType };
  }
  if (row.observationNoteId) {
    return { sourceId: row.observationNoteId, sourceType: "observation", date: row.observationDate, tag: row.observationTag, title: row.observationTitle };
  }
  if (row.meetingNoteId) {
    return { sourceId: row.meetingNoteId, sourceType: "meeting_note", date: row.meetingDate, title: row.meetingNoteTitle };
  }
  return null;
}

function formatChatTimestamp(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type ToolReadPart = {
  type: string;
  state?: string;
  input?: { sessionId?: string; formId?: string; observationId?: string; noteId?: string };
  output?: unknown;
};

function liveReadSources(part: ToolReadPart): ReadRowSource[] {
  if (part.state !== "output-available" || typeof part.output !== "string" || part.output.includes("<unavailable")) return [];
  if (part.type === "tool-readScorecard" && part.input?.sessionId && part.output.startsWith("<scorecard")) {
    const openTag = part.output.slice(0, part.output.indexOf(">") + 1);
    if (openTag.includes('status="pending"')) return [];
    return [{ sourceId: part.input.sessionId, sourceType: "session", date: xmlAttr(openTag, "date"), clientName: xmlAttr(openTag, "patient") }];
  }
  if (part.type === "tool-readForm" && part.input?.formId && part.output.startsWith("<form_snapshot")) {
    const openTag = part.output.slice(0, part.output.indexOf(">") + 1);
    const range = xmlAttr(openTag, "date_range");
    return [{ sourceId: part.input.formId, sourceType: "form", date: range?.slice(0, 10) ?? null, formType: xmlAttr(openTag, "type") }];
  }
  if (part.type === "tool-readMeetingNote" && part.input?.noteId && part.output.startsWith("<meeting_note")) {
    const openTag = part.output.slice(0, part.output.indexOf(">") + 1);
    return [{ sourceId: part.input.noteId, sourceType: "meeting_note", date: xmlAttr(openTag, "date"), title: xmlAttr(openTag, "title") }];
  }
  if (part.type === "tool-readObservations" && part.output.startsWith("<observation_notes")) {
    return [...part.output.matchAll(/<observation\s+([^>]+)>/g)].flatMap((match) => {
      const id = xmlAttr(match[1], "id");
      if (!id) return [];
      return [{ sourceId: id, sourceType: "observation" as const, date: xmlAttr(match[1], "date"), tag: xmlAttr(match[1], "tag") as ReadRowSource["tag"], title: xmlAttr(match[1], "title") }];
    });
  }
  return [];
}

function xmlAttr(text: string, name: string): string | null {
  return text.match(new RegExp(`${name}="([^"]*)"`))?.[1] ?? null;
}

/**
 * An assistant message rendered as its part sequence: markdown text segments
 * interleaved with grouped read logs, in the order the model produced them.
 * Consecutive reads collapse into one reads block per the comp's read-log
 * treatment.
 */
type AssistantSegment =
  | { type: "text"; text: string }
  | { type: "reads"; sources: ReadRowSource[] };

function buildAssistantSegments(message: ChatUIMessage): AssistantSegment[] {
  const segments: AssistantSegment[] = [];
  for (const part of message.parts) {
    if (part.type === "text" && part.text) {
      const last = segments[segments.length - 1];
      if (last?.type === "text") {
        last.text += part.text;
      } else {
        segments.push({ type: "text", text: part.text });
      }
    } else {
      const sources = liveReadSources(part as ToolReadPart);
      if (sources.length === 0) continue;
      const last = segments[segments.length - 1];
      if (last?.type === "reads") {
        for (const source of sources) {
          if (!last.sources.some((existing) => existing.sourceId === source.sourceId)) last.sources.push(source);
        }
      } else {
        segments.push({ type: "reads", sources });
      }
    }
  }
  return segments;
}

/** Timeline items: consecutive persisted read rows collapse into one log. */
type RenderItem =
  | { type: "reads"; key: string; sources: ReadRowSource[] }
  | { type: "message"; message: ChatUIMessage };

/** Hover action row shared by user and assistant messages: copy (+ retry). */
function MessageActions({
  copied,
  onCopy,
  timestamp,
  onRetry,
  retryDisabled,
}: {
  copied: boolean;
  onCopy: () => void;
  timestamp?: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="mt-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        {timestamp && (
          <time
            dateTime={timestamp}
            title={formatTimestamp(timestamp, "shortWithYearAndTime")}
            className="mr-1 text-[11px] leading-none text-sand-400"
          >
            {formatChatTimestamp(timestamp)}
          </time>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onCopy}
              className="h-[28px] w-[28px] rounded-md text-sand-400 hover:text-foreground hover:bg-sand-100"
            >
              {copied ? (
                <Check className="h-[14px] w-[14px] text-forest-500" />
              ) : (
                <Copy className="h-[14px] w-[14px]" />
              )}
              <span className="sr-only">Copy message</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{copied ? "Copied" : "Copy"}</TooltipContent>
        </Tooltip>
        {onRetry && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={retryDisabled}
                onClick={onRetry}
                className="h-[28px] w-[28px] rounded-md text-sand-400 hover:text-foreground hover:bg-sand-100"
              >
                <RotateCcw className="h-[14px] w-[14px]" />
                <span className="sr-only">Retry</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Retry</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

function isToolInFlight(message: ChatUIMessage, toolType: string): boolean {
  return message.parts.some((p) => {
    if (p.type !== toolType) return false;
    const state = (p as { state?: string }).state;
    return state === "input-streaming" || state === "input-available";
  });
}

export interface RdThreadChatProps {
  rdId: string;
  rdSlug: string;
  rdName: string;
  /** null = the /chats/new surface; the thread is created on first send. */
  chatId: string | null;
  threadTitle: string | null;
  threadKind: ChatKind;
  /** Thread creation timestamp — the header's "started Jul 2" meta. */
  threadCreatedAt?: string | null;
  initialMessages: ChatMessageRow[];
  sessions: SessionRowData[];
  /** True when an admin is viewing another owner's thread. */
  readOnly: boolean;
  ownerName?: string | null;
  /** "Ask about this" seed from ?source= — scopes the new thread's manifest. */
  source?: ReadSourceRef | null;
  /** Human-readable metadata for the focused source; never used for authorization. */
  sourceLabel?: string | null;
  /** Record inventory for the launcher's manifest line. */
  recordCounts?: {
    sessions: number;
    meetings: number | null;
    observations: number;
  };
  /** New chat starter prompt groups from the API-backed library. */
  starterPromptGroups?: StarterPromptGroup[];
  /**
   * Launcher-only: fires once the first turn is fully persisted (user row,
   * read rows, assistant row, derived title). The launcher page uses it to
   * swap to the thread route — the TanStack stand-in for the legacy shallow
   * replaceState + router.refresh() pair. Not called if the launcher was
   * unmounted mid-turn (the user navigated away).
   */
  onFirstTurnPersisted?: (chatId: string) => void;
}

interface LocalPill {
  localId: string;
  sessionId: string;
  sessionShortId: string;
  sessionDate: string | null;
  clientName: string | null;
}

export function RdThreadChat({
  rdId,
  rdSlug,
  rdName,
  chatId: initialChatId,
  threadTitle,
  threadKind,
  threadCreatedAt,
  initialMessages,
  sessions,
  readOnly,
  ownerName,
  source,
  sourceLabel,
  recordCounts,
  starterPromptGroups = [],
  onFirstTurnPersisted,
}: RdThreadChatProps) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [flyoverSessionId, setFlyoverSessionId] = useState<string | null>(null);
  const [recordFlyoverSource, setRecordFlyoverSource] = useState<ReadRowSource | null>(null);
  const [attachTranscript, setAttachTranscript] = useState<string | null>(null);
  const [localPills, setLocalPills] = useState<LocalPill[]>([]);
  const [analyzingSessionIds, setAnalyzingSessionIds] = useState<Set<string>>(new Set());
  const [localSessions, setLocalSessions] = useState<SessionRowData[]>([]);
  const [pasteBanner, setPasteBanner] = useState<{ charCount: number; text: string } | null>(null);
  const [displayTitle, setDisplayTitle] = useState(threadTitle?.trim() || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [renameValue, setRenameValue] = useState(threadTitle?.trim() || "");
  const [renaming, setRenaming] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [streamTimedOut, setStreamTimedOut] = useState(false);
  const [streamStopped, setStreamStopped] = useState(false);
  const stopRequestedRef = useRef(false);
  const skipRenameSaveRef = useRef(false);
  const streamAbortRef = useRef<{ controller: AbortController; timeout: ReturnType<typeof setTimeout> } | null>(null);

  // The thread is created lazily on first send (an abandoned /chats/new visit
  // leaves no row). The ref feeds the transport's URL; state drives UI.
  const chatIdRef = useRef<string | null>(initialChatId);
  const [chatId, setChatId] = useState<string | null>(initialChatId);
  const [creatingThread, setCreatingThread] = useState(false);

  // useChat's stream (and its onFinish) outlives an unmount; the launcher's
  // route swap must not fire if the user already navigated elsewhere.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const title = threadTitle?.trim() || "";
    setDisplayTitle(title);
    setRenameValue(title);
  }, [threadTitle]);

  useEffect(() => {
    if (!editingTitle) return;
    const frame = requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [editingTitle]);

  const cancelRename = useCallback(() => {
    skipRenameSaveRef.current = true;
    setRenameValue(displayTitle);
    setEditingTitle(false);
  }, [displayTitle]);

  const startRename = useCallback((title: string) => {
    skipRenameSaveRef.current = false;
    setRenameValue(title);
    setEditingTitle(true);
  }, []);

  const clearStreamTimeout = useCallback(() => {
    if (!streamAbortRef.current) return;
    clearTimeout(streamAbortRef.current.timeout);
    streamAbortRef.current = null;
  }, []);

  const abortActiveStream = useCallback(() => {
    const active = streamAbortRef.current;
    if (!active) return;
    clearTimeout(active.timeout);
    active.controller.abort();
    streamAbortRef.current = null;
  }, []);

  useEffect(() => () => clearStreamTimeout(), [clearStreamTimeout]);

  const sessionById = useMemo(() => {
    const map = new Map<string, SessionRowData>();
    for (const s of sessions) map.set(s.id, s);
    for (const s of localSessions) map.set(s.id, s);
    return map;
  }, [sessions, localSessions]);

  const sessionPointers = useMemo(() => {
    const map = new Map<string, SessionPointerData>();
    for (const row of initialMessages) {
      if (row.sessionId && row.kind === "message") {
        map.set(dbMessageId(row.id), {
          sessionId: row.sessionId,
          sessionDate: row.sessionDate,
          clientName: row.clientName,
          summary: row.summary,
          status: (row.sessionStatus ?? "pending") as AnalysisStatus,
          errorMessage: row.sessionErrorMessage,
        });
      }
    }
    return map;
  }, [initialMessages]);

  const sourcePointers = useMemo(() => {
    const map = new Map<string, ReadRowSource>();
    for (const row of initialMessages) {
      if (row.kind !== "message" || row.sessionId) continue;
      const source = sourceFromRow(row);
      if (source) map.set(dbMessageId(row.id), source);
    }
    return map;
  }, [initialMessages]);

  const persistedReadRows = useMemo(() => {
    const map = new Map<string, ReadRowSource>();
    for (const row of initialMessages) {
      if (row.kind !== "read") continue;
      const source = sourceFromRow(row);
      if (source) map.set(dbMessageId(row.id), source);
    }
    return map;
  }, [initialMessages]);

  const failedAssistants = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of initialMessages) {
      if (row.role === "assistant" && row.status === "error") {
        map.set(dbMessageId(row.id), row.errorMessage ?? "The AI response failed.");
      }
    }
    return map;
  }, [initialMessages]);

  const seedMessages = useMemo(
    () => dbToUIMessages(initialMessages),
    [initialMessages],
  );

  const transport = useMemo(
    () =>
      // The api value is a required placeholder that is never fetched — the
      // custom fetch always substitutes the real chat id from the ref (which
      // may not exist until the first send creates the thread). The sentinel
      // segment is deliberately not a plausible id.
      new DefaultChatTransport({
        api: `/api/rds/${rdId}/chats/__unset__/messages`,
        fetch: (async (_input: RequestInfo | URL, init?: RequestInit) => {
          const id = chatIdRef.current;
          if (!id) {
            throw new Error("Thread not created yet");
          }
          clearStreamTimeout();
          setStreamTimedOut(false);
          setStreamStopped(false);
          stopRequestedRef.current = false;
          const controller = new AbortController();
          const timeout = setTimeout(() => {
            if (!mountedRef.current) return;
            setStreamTimedOut(true);
            controller.abort();
            void queryClient.invalidateQueries({ queryKey: ["thread"] });
          }, CHAT_TURN_TIMEOUT_MS);
          streamAbortRef.current = { controller, timeout };
          init?.signal?.addEventListener("abort", () => controller.abort(), { once: true });
          const res = await fetchWithAuthRetry(
            getToken,
            `/api/rds/${rdId}/chats/${encodeURIComponent(id)}/messages`,
            { ...init, signal: controller.signal },
          );
          // The server persists the user row and (on a first send) the
          // derived title before the stream's first byte, so response
          // headers are the earliest point the sidebar can list the titled
          // thread. Waiting for onFinish would keep it hidden until the turn
          // ends — and on an errored turn, indefinitely.
          if (res.ok) {
            void queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
          }
          return res;
        }) as typeof fetch,
      }),
    [clearStreamTimeout, getToken, rdId, queryClient],
  );

  const { messages, sendMessage, status, regenerate, stop, error } = useChat<ChatUIMessage>({
    transport,
    messages: seedMessages,
    onFinish: () => {
      clearStreamTimeout();
      setStreamTimedOut(false);
      setStreamStopped(false);
      stopRequestedRef.current = false;
      // The turn (user row, read rows, assistant row) is persisted now — a
      // safe point to sync the server tree and refresh the sidebar's thread
      // recency. Key prefix only: the sidebar and surface queries key by the
      // route param (slug or UUID), while this component holds the canonical
      // id — an exact key would silently miss.
      void queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
      // First send on the launcher: hand off to the thread route, which now
      // renders identical DB-backed content (the legacy router.refresh()
      // semantics). Doing this any earlier would remount against an
      // incomplete thread and tear down the live stream.
      if (!initialChatId && chatIdRef.current && mountedRef.current) {
        onFirstTurnPersisted?.(chatIdRef.current);
      }
    },
    onError: (err) => {
      clearStreamTimeout();
      // Refresh the DB-backed row when the server managed to persist an error;
      // the live failure card still covers purely client-side failures.
      if (chatIdRef.current) {
        void queryClient.invalidateQueries({ queryKey: ["thread"] });
      }
      if (stopRequestedRef.current) return;
      Sentry.captureException(err, {
        tags: { surface: "chat", source: "client", route: "rds.thread-chat" },
      });
    },
  });

  const displayMessages = readOnly ? seedMessages : messages;
  const isStreaming = status === "streaming" || status === "submitted";
  const isAnalyzing = analyzingSessionIds.size > 0;

  // The thread's read set: persisted read rows plus this session's live tool
  // reads. Drives the ledger line and cite-token validation.
  const readSources = useMemo(() => {
    const map = new Map<string, ReadRowSource>();
    for (const source of persistedReadRows.values()) {
      map.set(source.sourceId, source);
    }
    for (const message of displayMessages) {
      if (message.role !== "assistant") continue;
      for (const part of message.parts) {
        const toolPart = part as ToolReadPart;
        for (const source of liveReadSources(toolPart)) {
          if (!map.has(source.sourceId)) map.set(source.sourceId, source);
        }
      }
    }
    return [...map.values()];
  }, [persistedReadRows, displayMessages, sessionById]);

  const readSessionIds = useMemo(
    () => new Set(readSources.map((s) => s.sourceId)),
    [readSources],
  );

  const renderItems = useMemo(() => {
    const items: RenderItem[] = [];
    for (const message of displayMessages) {
      const readRow = persistedReadRows.get(message.id);
      if (readRow) {
        const source: ReadRowSource = {
          ...readRow,
            sessionType: readRow.sourceType === "session"
              ? sessionById.get(readRow.sourceId)?.sessionType ?? null
              : null,
        };
        const last = items[items.length - 1];
        if (last?.type === "reads") {
          if (!last.sources.some((s) => s.sourceId === source.sourceId)) {
            last.sources.push(source);
          }
        } else {
          items.push({ type: "reads", key: `reads-${message.id}`, sources: [source] });
        }
      } else {
        items.push({ type: "message", message });
      }
    }
    return items;
  }, [displayMessages, persistedReadRows, sessionById]);

  const handleOpenSource = useCallback((source: ReadRowSource) => {
    if (source.sourceType === "session" && !source.deleted) {
      setRecordFlyoverSource(null);
      setFlyoverSessionId(source.sourceId);
    } else {
      setFlyoverSessionId(null);
      setRecordFlyoverSource(source);
    }
  }, []);

  const handleCloseFlyover = useCallback(() => {
    setFlyoverSessionId(null);
    setRecordFlyoverSource(null);
  }, []);

  const flyoverSession = useMemo(
    () => (flyoverSessionId ? sessionById.get(flyoverSessionId) ?? null : null),
    [flyoverSessionId, sessionById],
  );

  const readSourceFor = useCallback(
    (sessionId: string, fallback?: Partial<ReadRowSource>): ReadRowSource => {
      const meta = sessionById.get(sessionId);
      return {
        sourceId: sessionId,
        sourceType: "session",
        date: meta?.sessionDate ?? fallback?.date ?? null,
        clientName: meta?.clientName ?? fallback?.clientName ?? null,
        sessionType: meta?.sessionType ?? null,
      };
    },
    [sessionById],
  );

  const citeLabelFor = useCallback(
    (sessionId: string) => {
      const source = readSources.find((candidate) => candidate.sourceId === sessionId);
      if (source && source.sourceType !== "session") {
        return source.deleted ? readRowLabel(source) : source.title || readRowLabel(source);
      }
      const meta = sessionById.get(sessionId);
      const date = formatLocalDate(meta?.sessionDate ?? null) || "Session";
      return meta?.clientName ? `${date} · ${meta.clientName}` : date;
    },
    [sessionById, readSources],
  );

  // The comp's chat typography: 14px/1.6 sand-600 body with foreground
  // emphasis inside ai-cards.
  const assistantMarkdownComponents: Components = useMemo(
    () => ({
      p: ({ children }) => (
        <p className="mb-[10px] text-sm leading-[1.6] text-sand-600 last:mb-0">
          {children}
        </p>
      ),
      strong: ({ children }) => (
        <strong className="font-semibold text-foreground">{children}</strong>
      ),
      ul: ({ children }) => (
        <ul className="mb-[10px] list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-[10px] list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
      ),
      li: ({ children }) => (
        <li className="text-sm leading-[1.6] text-sand-600">{children}</li>
      ),
      a: ({ href, children }) => {
        const citedSessionId = href ? parseCiteHref(href) : null;
        if (citedSessionId) {
          const open =
            flyoverSessionId === citedSessionId ||
            recordFlyoverSource?.sourceId === citedSessionId;
          const citedSource = readSources.find((source) => source.sourceId === citedSessionId);
          return (
            <button
              type="button"
              onClick={() => handleOpenSource(citedSource ?? readSourceFor(citedSessionId))}
              aria-label={`Open cited source: ${citeLabelFor(citedSessionId)}`}
              className={cn(
                "my-[2px] mr-1 inline-flex min-h-[24px] items-center gap-[5px] rounded-full border px-[9px] align-baseline text-[11.5px] font-semibold text-interactive transition-colors",
                open
                  ? "border-interactive bg-interactive-light"
                  : "border-sand-200 bg-card hover:border-interactive",
              )}
            >
              {children}
            </button>
          );
        }
        return (
          <a href={href} target="_blank" rel="noreferrer" className="underline">
            {children}
          </a>
        );
      },
    }),
    [handleOpenSource, citeLabelFor, flyoverSessionId, recordFlyoverSource, readSources, readSourceFor],
  );

  const userMarkdownComponents: Components = useMemo(
    () => ({
      p: ({ children }) => (
        <p className="mb-2 text-sm leading-[1.55] text-foreground last:mb-0">
          {children}
        </p>
      ),
      li: ({ children }) => (
        <li className="text-sm leading-[1.55] text-foreground">{children}</li>
      ),
    }),
    [],
  );

  const renderAssistantText = useCallback(
    (text: string) => (
      <MarkdownRenderer
        content={renderCiteTokens(text, readSessionIds, citeLabelFor)}
        components={assistantMarkdownComponents}
      />
    ),
    [readSessionIds, citeLabelFor, assistantMarkdownComponents],
  );

  const lastUserMessageId = useMemo(() => {
    if (readOnly) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "user" && !sessionPointers.has(m.id) && getTextContent(m)) {
        return m.id;
      }
    }
    return null;
  }, [messages, sessionPointers, readOnly]);

  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const handleCopyMessage = useCallback((message: ChatUIMessage) => {
    // Copy the prose without cite tokens — the clipboard shouldn't carry the
    // machine contract or internal session ids.
    const text = stripCiteTokens(getTextContent(message));
    if (!text) return;
    navigator.clipboard.writeText(text).catch((err) => {
      Sentry.captureException(err, {
        tags: { surface: "chat", source: "client", action: "copy_message" },
      });
    });
    setCopiedMessageId(message.id);
    setTimeout(() => {
      setCopiedMessageId((prev) => (prev === message.id ? null : prev));
    }, 2000);
  }, []);

  // Follow-the-tail scroll, mirroring the mono-chat behavior.
  const isAtBottomRef = useRef(true);
  const [hasUnread, setHasUnread] = useState(false);

  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    if (atBottom) setHasUnread(false);
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    isAtBottomRef.current = true;
    setHasUnread(false);
  }, []);

  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    if (isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    } else {
      const frame = requestAnimationFrame(() => {
        if (!isAtBottomRef.current) setHasUnread(true);
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [messages, localPills]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }, [inputValue]);

  const isSendDisabled = isStreaming || isAnalyzing || creatingThread;

  // Source pills and starter rows fill the composer and focus it for editing —
  // never send. The supervisor always reviews before submitting.
  const fillComposer = useCallback((text: string) => {
    setInputValue(text);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      // Pre-select the first [slot] so typing replaces it directly.
      const slot = /\[[^\]]*\]/.exec(text);
      if (slot) {
        textarea.setSelectionRange(slot.index, slot.index + slot[0].length);
      }
    });
  }, []);

  const ensureThread = useCallback(async (): Promise<boolean> => {
    if (chatIdRef.current) return true;
    setCreatingThread(true);
    try {
      const res = await fetchWithAuthRetry(getToken, `/api/rds/${rdId}/chats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(source ? { source } : {}),
      });
      if (!res.ok) {
        Sentry.captureMessage("thread creation rejected", {
          tags: {
            surface: "chat",
            source: "client",
            route: "rds.thread-chat",
            action: "create_thread",
            status: String(res.status),
          },
        });
        toast.error("Couldn't create the thread. Try again.");
        return false;
      }
      const created: { id: string } = await res.json();
      chatIdRef.current = created.id;
      setChatId(created.id);
      // The URL deliberately stays on /chats/new until the turn finishes.
      // The legacy app swapped it here with a shallow replaceState, but
      // TanStack's history monkey-patches window.history.replaceState, so a
      // raw swap re-matches the route immediately — remounting the thread
      // page against a still-empty DB and tearing down this instance's live
      // stream. The route handoff happens in useChat's onFinish instead,
      // once the turn is persisted (onFirstTurnPersisted).
      return true;
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          surface: "chat",
          source: "client",
          route: "rds.thread-chat",
          action: "create_thread",
        },
      });
      toast.error("Couldn't create the thread. Try again.");
      return false;
    } finally {
      setCreatingThread(false);
    }
  }, [getToken, rdId, source]);

  async function submitText(text: string) {
    if (!text.trim() || isSendDisabled) return;
    if (!(await ensureThread())) return;
    sendMessage({
      text,
      metadata: { createdAt: new Date().toISOString() },
    });
    isAtBottomRef.current = true;
    setHasUnread(false);
    setInputValue("");
    setPasteBanner(null);
  }

  async function handleSend(e?: { preventDefault?: () => void }) {
    e?.preventDefault?.();
    await submitText(inputValue);
  }

  const handleRetryLastUserMessage = useCallback(() => {
    setStreamTimedOut(false);
    setStreamStopped(false);
    stopRequestedRef.current = false;
    regenerate();
    isAtBottomRef.current = true;
    setHasUnread(false);
  }, [regenerate]);

  const handleStopResponse = useCallback(() => {
    if (!isStreaming) return;
    stopRequestedRef.current = true;
    setStreamTimedOut(false);
    setStreamStopped(true);
    stop();
    abortActiveStream();
    if (chatIdRef.current) {
      void queryClient.invalidateQueries({ queryKey: ["thread"] });
    }
  }, [abortActiveStream, isStreaming, queryClient, rdId, stop]);

  const handleRenameThread = useCallback(async () => {
    if (skipRenameSaveRef.current) {
      skipRenameSaveRef.current = false;
      return;
    }
    if (!chatId || readOnly || renaming) return;
    const title = renameValue.trim();
    const currentTitle = displayTitle.trim();
    if (title === currentTitle) {
      setEditingTitle(false);
      return;
    }
    if (!title) {
      toast.error("Enter a chat name.");
      return;
    }
    if (title.length > MAX_THREAD_TITLE_LENGTH) {
      toast.error("Chat names must be 120 characters or fewer.");
      return;
    }
    setRenaming(true);
    try {
      const res = await fetchWithAuthRetry(getToken, `/api/rds/${rdId}/chats/${encodeURIComponent(chatId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error(`Rename failed with ${res.status}`);
      setDisplayTitle(title);
      setRenameValue(title);
      setEditingTitle(false);
      await queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
      await queryClient.invalidateQueries({ queryKey: ["thread"] });
      toast.success("Chat renamed");
    } catch (err) {
      Sentry.captureException(err, {
        tags: { surface: "chat", source: "client", route: "rds.thread-chat", action: "rename_thread" },
      });
      toast.error("Couldn't rename the chat. Try again.");
    } finally {
      setRenaming(false);
    }
  }, [chatId, displayTitle, getToken, rdId, readOnly, renameValue, renaming, queryClient]);

  const handleDeleteThread = useCallback(async () => {
    if (!chatId || readOnly || deleting) return;
    setDeleting(true);
    try {
      const res = await fetchWithAuthRetry(getToken, `/api/rds/${rdId}/chats/${encodeURIComponent(chatId)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Delete failed with ${res.status}`);
      await queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
      toast.success("Chat deleted");
      void navigate({
        to: "/rds/$rdId/chats/new",
        params: { rdId: rdSlug },
        replace: true,
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { surface: "chat", source: "client", route: "rds.thread-chat", action: "delete_thread" },
      });
      toast.error("Couldn't delete the chat. Try again.");
    } finally {
      setDeleting(false);
    }
  }, [chatId, deleting, getToken, navigate, queryClient, rdId, rdSlug, readOnly]);

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const text = e.clipboardData.getData("text");
    if (text.length > LARGE_PASTE_THRESHOLD) {
      setPasteBanner({ charCount: text.length, text });
    }
  }

  function handleAttachFromBanner() {
    setAttachTranscript(pasteBanner?.text ?? "");
    setPasteBanner(null);
    setInputValue("");
  }

  const handleAttachSubmit = useCallback(
    (result: { sessionId: string; sessionShortId: string; clientName: string | null; sessionDate: string | null }) => {
      const localId = crypto.randomUUID();
      setLocalPills((prev) => [...prev, { localId, ...result }]);
      setAnalyzingSessionIds((prev) => new Set(prev).add(result.sessionId));
      setAttachTranscript(null);
      // Legacy router.refresh(): pull the new session into the server tree —
      // the sidebar counts, the RD sessions table, and the workspace summary.
      // Key prefixes only — those queries key by the route param (slug or
      // UUID), not the canonical id this component holds.
      void queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
      void queryClient.invalidateQueries({ queryKey: ["rd-sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["rd"] });
    },
    [queryClient],
  );

  const dbSessionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const pointer of sessionPointers.values()) {
      ids.add(pointer.sessionId);
    }
    return ids;
  }, [sessionPointers]);

  const handlePillStatusChange = useCallback(
    (pill: LocalPill, pillStatus: "complete" | "error") => {
      setAnalyzingSessionIds((prev) => {
        const next = new Set(prev);
        next.delete(pill.sessionId);
        return next;
      });

      if (pillStatus === "complete") {
        const label = pill.clientName || "Session";
        const datePart = pill.sessionDate ? ` ${pill.sessionDate}` : "";
        toast.success(`${label}${datePart} added`);

        setLocalSessions((prev) => [
          ...prev,
          {
            id: pill.sessionId,
            shortId: pill.sessionShortId,
            clientName: pill.clientName,
            sessionDate: pill.sessionDate,
            sessionType: null,
            summary: null,
            promptVersion: "unknown",
            status: "complete" as const,
            errorMessage: null,
            patientId: null,
          },
        ]);
      }
    },
    [],
  );

  const handlePillDismiss = useCallback((localId: string, sessionId: string) => {
    setLocalPills((prev) => prev.filter((p) => p.localId !== localId));
    setAnalyzingSessionIds((prev) => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  }, []);

  const focusSession = source?.type === "session" ? sessionById.get(source.id) ?? null : null;
  const focusLabel = sourceLabel ?? (source
    ? source.type === "session" && focusSession
      ? `${formatLocalDate(focusSession.sessionDate) || "Session"} scorecard — ${focusSession.clientName ?? "unknown"}`
      : source.type === "meeting_note"
        ? "Focused meeting note"
        : source.type === "observation"
          ? "Focused observation"
          : source.type === "form"
            ? "Focused form"
            : "Focused session"
    : null);
  const sourcePrompts = source ? sourceStarterPrompts(source.type) : [];

  // The launcher hello names a focused form by its week ("Ask about the week
  // of Jul 7 weekly form"); other source types read naturally as type nouns.
  const helloHeading = (() => {
    if (!focusLabel) {
      return `Where would you like to start with ${rdFirstName(rdName)}?`;
    }
    if (source?.type === "form") {
      return `Ask about the ${focusLabel.charAt(0).toLowerCase()}${focusLabel.slice(1)}`;
    }
    if (source?.type === "meeting_note") return "Ask about this meeting note";
    return `Ask about this ${source?.type ?? "source"}`;
  })();

  // Launcher-only optimistic title: the server derives the persisted title
  // from the first user message with this same shared helper, so the header
  // can name the thread the moment the first send goes out instead of at the
  // turn-end route handoff. Focus threads keep their scorecard label — the
  // server titles those at creation and never overwrites.
  const optimisticTitle = useMemo(() => {
    if (initialChatId !== null || displayTitle || threadKind !== "adhoc") return null;
    for (const message of messages) {
      if (message.role === "user") {
        const text = getTextContent(message);
        if (text) return deriveThreadTitle(text);
      }
    }
    return null;
  }, [initialChatId, displayTitle, threadKind, messages]);

  const headerTitle =
    displayTitle ||
    (focusLabel
      ? focusLabel
      : optimisticTitle ?? "New chat");
  const kindLabel = threadKind === "playbook_run" ? "Playbook run" : "Ad-hoc chat";
  // The launcher (an empty new thread) has no chat head — the hello block
  // owns the surface until the first send creates the thread.
  const showHeader = chatId !== null || displayMessages.length > 0;

  const manifestLine = recordCounts
    ? [
        `${recordCounts.sessions} session scorecard${recordCounts.sessions === 1 ? "" : "s"}`,
        recordCounts.meetings !== null
          ? `${recordCounts.meetings} meeting${recordCounts.meetings === 1 ? "" : "s"}`
          : null,
        `${recordCounts.observations} observation${recordCounts.observations === 1 ? "" : "s"}`,
      ]
        .filter((part): part is string => part !== null)
        .join(" · ")
    : null;

  return (
    <div className="flex h-full min-h-0 relative">
      {/* Chat column */}
      <div className="flex flex-col h-full min-w-0 flex-1 relative">
        {showHeader && (
          <header className="flex shrink-0 items-end justify-between gap-4 border-b border-sand-200 px-[28px] pb-[13px] pt-[18px]">
            <div className="min-w-0">
              {!readOnly && chatId && editingTitle ? (
                <input
                  ref={titleInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => void handleRenameThread()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      cancelRename();
                    }
                  }}
                  disabled={renaming}
                  maxLength={MAX_THREAD_TITLE_LENGTH}
                  aria-label="Chat name"
                  className="w-full min-w-[260px] max-w-[620px] rounded-none border border-interactive bg-transparent px-1 py-0 font-sans text-[17px] font-bold leading-[1.3] text-foreground outline outline-2 outline-interactive/40 disabled:opacity-60"
                />
              ) : !readOnly && chatId ? (
                <button
                  type="button"
                  onClick={() => startRename(headerTitle)}
                  className="block max-w-full truncate rounded-sm text-left font-sans text-[17px] font-bold leading-[1.3] text-foreground hover:text-interactive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="Rename chat"
                >
                  {headerTitle}
                </button>
              ) : (
                <h3 className="truncate font-sans text-[17px] font-bold leading-[1.3] text-foreground">
                  {headerTitle}
                </h3>
              )}
              <p className="mt-1 text-[12.5px] leading-[1.45] text-sand-500">
                {kindLabel}
                {threadCreatedAt ? ` · started ${formatTimestamp(threadCreatedAt, "short")}` : ""}
                {readOnly && ownerName ? ` · ${ownerName}’s thread` : ""}
              </p>
            </div>
            {!readOnly && chatId && (
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                  className="h-8 rounded-md px-2 text-xs text-sand-500 hover:bg-error/10 hover:text-error"
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Delete
                </Button>
              </div>
            )}
          </header>
        )}

        {/* Messages */}
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-[28px] pt-[22px] pb-[14px] min-h-0"
        >
          <div
            className={cn(
              "mx-auto space-y-[18px]",
              flyoverSession || recordFlyoverSource ? "max-w-[640px]" : "max-w-3xl",
            )}
          >
            {!readOnly && displayMessages.length === 0 && (
              <div className="pt-2 text-center">
                <h2 className="font-sans text-xl font-bold text-foreground">
                  {helloHeading}
                </h2>
                <p className="mx-auto mt-2 max-w-[580px] text-[13px] leading-[1.55] text-sand-500">
                  {manifestLine
                    ? `AlwaysAI knows the shape of ${rdName}’s record — ${manifestLine} — and reads sources only when the work needs them.`
                    : "AlwaysAI knows the shape of this RD’s record and reads sources only when the work needs them — every source consulted shows up in the thread."}
                </p>
                {source && (
                  <div className="mx-auto mt-6 max-w-[620px] rounded-2xl border border-ai/25 bg-ai-light/60 p-4 text-left shadow-sm">
                    <p className="text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-ai">
                      Attached source
                    </p>
                    <p className="mt-1 font-sans text-sm font-semibold text-foreground">
                      {focusLabel}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-sand-500">
                      AlwaysAI can read this source on demand. Choose a starting question or write your own below.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {sourcePrompts.map((prompt) => (
                        <button
                          key={prompt}
                          type="button"
                          onClick={() => fillComposer(prompt)}
                          className="rounded-full border border-ai/25 bg-white px-3 py-1.5 text-xs font-semibold text-ai transition-colors hover:border-ai hover:bg-ai-light"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {!source && (
                  <StarterPromptGroups
                    groups={starterPromptGroups}
                    rdName={rdName}
                    onSelectPrompt={fillComposer}
                  />
                )}
              </div>
            )}

            {readOnly && displayMessages.length === 0 && (
              <div className="flex items-center justify-center pt-8 pb-4 text-sm text-sand-400">
                No messages in this thread yet.
              </div>
            )}

            {renderItems.map((item) => {
              if (item.type === "reads") {
                return (
                  <ReadsBlock
                    key={item.key}
                    sources={item.sources}
                    onOpen={handleOpenSource}
                  />
                );
              }

              const { message } = item;
              const pointer = sessionPointers.get(message.id);
              if (pointer) {
                return (
                  <div key={message.id} className="flex justify-start">
                    <AnalysisPill
                      sessionId={pointer.sessionId}
                      sessionDate={pointer.sessionDate}
                      clientName={pointer.clientName}
                      summary={pointer.summary}
                      status={pointer.status}
                      errorMessage={pointer.errorMessage}
                      onOpenScorecard={(sessionId) => handleOpenSource(readSourceFor(sessionId))}
                    />
                  </div>
                );
              }

              const sourcePointer = sourcePointers.get(message.id);
              if (sourcePointer) {
                return (
                  <button
                    key={message.id}
                    type="button"
                    disabled={sourcePointer.deleted}
                    onClick={() => handleOpenSource(sourcePointer)}
                    className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-semibold text-sand-600 hover:border-interactive disabled:cursor-default disabled:text-sand-400"
                  >
                    Attached · {readRowLabel(sourcePointer)}
                  </button>
                );
              }

              if (message.role === "user") {
                const text = getTextContent(message);
                const isLastUser = message.id === lastUserMessageId;
                const copied = copiedMessageId === message.id;
                const createdAt = message.metadata?.createdAt;
                return (
                  <div key={message.id} className="group flex flex-col items-end">
                    <div className="max-w-[80%] rounded-[14px] bg-sand-100 px-4 py-3 text-foreground">
                      <MarkdownRenderer content={text} components={userMarkdownComponents} />
                    </div>
                    <MessageActions
                      copied={copied}
                      onCopy={() => handleCopyMessage(message)}
                      timestamp={createdAt}
                      onRetry={
                        isLastUser && !readOnly
                          ? handleRetryLastUserMessage
                          : undefined
                      }
                      retryDisabled={isSendDisabled}
                    />
                  </div>
                );
              }

              const failureText = failedAssistants.get(message.id);
              if (failureText) {
                return (
                  <div key={message.id} className="group">
                    <p className="mb-[6px] text-xs font-semibold text-sand-400">
                      AlwaysAI
                    </p>
                    <div className="rounded-[14px] border border-error/30 bg-card px-4 py-[14px] text-sm text-error">
                      {failureText} Use the retry button on your message above.
                    </div>
                  </div>
                );
              }

              const segments = buildAssistantSegments(message);
              const text = getTextContent(message);
              const isActiveAssistant = !readOnly && isStreaming && message.id === messages[messages.length - 1]?.id;
              const readingScorecard = isToolInFlight(message, "tool-readScorecard");
              const readingForm = isToolInFlight(message, "tool-readForm");
              const readingObservations = isToolInFlight(message, "tool-readObservations");
              const readingMeetingNote = isToolInFlight(message, "tool-readMeetingNote");
              const exploringTranscripts = isToolInFlight(message, "tool-exploreTranscripts");
              const busy = isActiveAssistant && (readingScorecard || readingForm || readingObservations || readingMeetingNote || exploringTranscripts);
              const copied = copiedMessageId === message.id;
              const emptyInactiveAssistant = segments.length === 0 && !isActiveAssistant;

              return (
                <div key={message.id} className="group">
                  <p className="mb-[6px] text-xs font-semibold text-sand-400">
                    AlwaysAI
                  </p>
                  <div className="space-y-[14px]">
                    {segments.map((segment, i) =>
                      segment.type === "reads" ? (
                        <ReadsBlock
                          key={`${message.id}-reads-${i}`}
                          sources={segment.sources.map((item) => item.sourceType === "session" ? readSourceFor(item.sourceId, item) : item)}
                          onOpen={handleOpenSource}
                        />
                      ) : (
                        <div
                          key={`${message.id}-text-${i}`}
                          className="rounded-[14px] border border-sand-200 bg-card px-4 py-[14px] shadow-sm"
                        >
                          {renderAssistantText(segment.text)}
                        </div>
                      ),
                    )}
                    {emptyInactiveAssistant ? (
                      <div className="rounded-[14px] border border-error/30 bg-card px-4 py-[14px] text-sm text-error">
                        No response was saved for this turn.{" "}
                        {readOnly ? "The thread owner can retry it." : "Use the retry button on your message above."}
                      </div>
                    ) : (isActiveAssistant && (segments.length === 0 || busy)) && (
                      <div className="rounded-[14px] border border-sand-200 bg-card px-4 py-[14px] shadow-sm">
                        <div className="flex items-center gap-3 text-sm text-sand-400">
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>
                              {readingScorecard
                                ? "Reading scorecard..."
                                : readingForm
                                  ? "Reading form..."
                                  : readingObservations
                                    ? "Reading observations..."
                                    : readingMeetingNote
                                      ? "Reading meeting note..."
                                : exploringTranscripts
                                  ? "Reading transcripts..."
                                  : "Thinking..."}
                            </span>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={handleStopResponse}
                            className="h-7 rounded-md px-2 text-xs text-sand-500 hover:bg-sand-100 hover:text-foreground"
                          >
                            <Square className="h-3 w-3" />
                            Stop
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {text && (
                    <MessageActions
                      copied={copied}
                      onCopy={() => handleCopyMessage(message)}
                    />
                  )}
                </div>
              );
            })}

            {/* Locally-tracked pills from chat-attach submissions */}
            {!readOnly && localPills
              .filter((pill) => !dbSessionIds.has(pill.sessionId))
              .map((pill) => (
                <div key={pill.localId} className="flex justify-start">
                  <AnalysisPill
                    sessionId={pill.sessionId}
                    sessionDate={pill.sessionDate}
                    clientName={pill.clientName}
                    summary={null}
                    status="pending"
                    errorMessage={null}
                    onOpenScorecard={(sessionId) => handleOpenSource(readSourceFor(sessionId))}
                    onStatusChange={(s) => handlePillStatusChange(pill, s)}
                    onDismiss={() => handlePillDismiss(pill.localId, pill.sessionId)}
                  />
                </div>
              ))}

            {!readOnly && isStreaming && messages[messages.length - 1]?.role === "user" && (
              <div>
                <p className="mb-[6px] text-xs font-semibold text-sand-400">
                  AlwaysAI
                </p>
                <div className="rounded-[14px] border border-sand-200 bg-card px-4 py-[14px] shadow-sm">
                  <div className="flex items-center gap-3 text-sm text-sand-400">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Thinking...</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={handleStopResponse}
                      className="h-7 rounded-md px-2 text-xs text-sand-500 hover:bg-sand-100 hover:text-foreground"
                    >
                      <Square className="h-3 w-3" />
                      Stop
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Live failure card — without this, a failed turn is invisible
                until reload rebuilds it from the persisted error row. The
                message text is the server's humanized sentinel (fixed
                literals), passed through the stream's onError. */}
            {!readOnly && (status === "error" || streamTimedOut || streamStopped) && (
              <div>
                <p className="mb-[6px] text-xs font-semibold text-sand-400">
                  AlwaysAI
                </p>
                <div className="rounded-[14px] border border-error/30 bg-card px-4 py-[14px] text-sm text-error">
                  <p>
                    {streamTimedOut
                      ? "The response took too long and was stopped."
                      : streamStopped
                        ? "The response was stopped."
                      : error?.message || "The AI response failed. Try again."}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleRetryLastUserMessage}
                    disabled={isSendDisabled}
                    className="mt-3 border-error/30 text-error hover:bg-error/10 hover:text-error"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Retry
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Attach form overlay */}
        {attachTranscript !== null && chatId && (
          <TranscriptForm
            endpoint={`/api/rds/${rdId}/chat/attach?chatId=${encodeURIComponent(chatId)}`}
            variant="overlay"
            title="Attach session transcript"
            subtitle="will be analyzed and appear in the thread"
            submitLabel="Attach & Analyze"
            helperText="Analysis takes about 2 minutes. You can keep chatting while it runs."
            initialTranscript={attachTranscript}
            onSuccess={handleAttachSubmit}
            onClose={() => setAttachTranscript(null)}
          />
        )}

        {hasUnread && (
          <button
            type="button"
            onClick={() => scrollToBottom("smooth")}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-[6px] rounded-full border border-sand-200 bg-card px-3 py-1 text-xs font-medium text-foreground shadow-md hover:bg-sand-50 whitespace-nowrap"
          >
            <ArrowDown className="h-3 w-3" />
            New messages
          </button>
        )}

        {/* Read-only banner */}
        {readOnly && (
          <div className="shrink-0 px-[28px] pb-[18px] pt-2">
            <div
              className={cn(
                "mx-auto flex w-full items-center gap-2 rounded-[18px] border border-sand-200 bg-sand-100 px-4 py-3 text-sm text-sand-500",
                flyoverSession || recordFlyoverSource ? "max-w-[640px]" : "max-w-3xl",
              )}
            >
              <Lock className="h-4 w-4 text-sand-400 shrink-0" />
              <span>
                Read-only &mdash; {ownerName ?? "another user"}&rsquo;s thread on this RD
              </span>
            </div>
          </div>
        )}

        {/* Composer */}
        {!readOnly && (
          <div className="shrink-0 px-[28px] pb-[18px] pt-2">
            <form
              onSubmit={handleSend}
              className={cn(
                "mx-auto w-full",
                flyoverSession || recordFlyoverSource ? "max-w-[640px]" : "max-w-3xl",
              )}
            >
              <SourceLedgerLine
                sources={readSources}
                isNewThread={!chatId}
                onOpen={handleOpenSource}
              />
              <div className="flex min-h-[56px] items-end gap-[10px] rounded-[18px] border border-sand-200 bg-card px-3 py-[10px] shadow-sm">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  disabled={!chatId}
                  onClick={() => setAttachTranscript("")}
                  className="h-[34px] w-[34px] shrink-0 rounded-[10px] border border-sand-200 bg-background text-sand-500 hover:text-foreground disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" />
                  <span className="sr-only">Attach transcript</span>
                </Button>
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onPaste={handlePaste}
                  placeholder={
                    isStreaming
                      ? "Draft your next message..."
                      : chatId
                        ? `Ask about ${rdName}…`
                        : `Or ask your own question about ${rdName}…`
                  }
                  style={{ outline: "none", boxShadow: "none" }}
                  className="flex-1 resize-none border-none bg-transparent text-sm leading-5 h-[34px] max-h-[200px] px-0 py-[7px] placeholder:text-sand-400 disabled:cursor-not-allowed disabled:opacity-50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                {isStreaming ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={handleStopResponse}
                          className="h-[34px] w-[34px] shrink-0 rounded-[10px] bg-sand-100 text-foreground hover:bg-sand-200"
                        >
                          <Square className="h-4 w-4" />
                          <span className="sr-only">Stop response</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Stop response</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : isAnalyzing ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button
                            type="submit"
                            size="icon"
                            variant="ghost"
                            disabled
                            className="h-[34px] w-[34px] shrink-0 rounded-[10px] bg-sand-100 text-foreground"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Waiting for transcript analysis to complete
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    variant="ghost"
                    disabled={!inputValue.trim() || isSendDisabled}
                    className={cn(
                      "h-[34px] w-[34px] shrink-0 rounded-[10px] bg-sand-100 text-foreground hover:bg-sand-200 disabled:bg-sand-50 disabled:text-sand-300",
                    )}
                  >
                    {creatingThread ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUp className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
              {pasteBanner && (
                <div className="mt-2">
                  <LargePasteBanner
                    charCount={pasteBanner.charCount}
                    onAttach={handleAttachFromBanner}
                    onDismiss={() => setPasteBanner(null)}
                  />
                </div>
              )}
            </form>
            <p
              className={cn(
                "mx-auto mt-2 text-xs text-sand-400",
                flyoverSession || recordFlyoverSource ? "max-w-[640px]" : "max-w-3xl",
              )}
            >
              Enter to send, Shift+Enter for new line
            </p>
          </div>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={(open) => !deleting && setDeleteOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this chat?</DialogTitle>
            <DialogDescription>
              This removes the thread and its messages for this RD. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void handleDeleteThread()} disabled={deleting}>
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SourceFlyover
        rdId={rdId}
        rdSlug={rdSlug}
        session={flyoverSession}
        onClose={handleCloseFlyover}
      />
      <RecordSourceFlyover
        rdId={rdId}
        rdSlug={rdSlug}
        source={recordFlyoverSource}
        onClose={handleCloseFlyover}
      />
    </div>
  );
}
